import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useToast} from '../context/ToastContext';
import {useNotification} from '../context/NotificationContext';
import {useAuth} from '../providers/AuthProvider';
import {useUserNickname} from '../hooks/useUserNickname';
import {colors} from '../theme/colors';

const ProfileScreen = () => {
  const navigate = useNavigate();
  const {success, error} = useToast();
  const {isSubscribed, subscribe, unsubscribe, sendTestNotification} = useNotification();
  const {signOut, user} = useAuth();
  const userNickname = useUserNickname();
  const SHOW_LINK = 'https://open.spotify.com/show/4RPIM3oVZnm3C9abLG1Z3q';

  const RELAXING_LINES = [
    'היי, קצב עדין היום. נשימה אחת בכל פעם.',
    'לא צריך להספיק הכל. מספיק צעד קטן שמרגיש טוב.',
    'רכות היא כוח. גם שקט נחשב התקדמות.',
    'אם זה מרגיש כבד—נוריד הילוך ביחד.',
    'היום בוחרים בטוב: מים, אוויר, ותנועה קטנה.',
  ];

  const MOODS = ['רגוע', 'בסדר', 'לחוץ'];
  const HABITS = [
    {k: 'water', t: 'שתיתי מים'},
    {k: 'walk', t: 'הלכתי 10 דק׳'},
    {k: 'stretch', t: 'מתיחות קצרות'},
    {k: 'earlySleep', t: 'כיבוי מסכים מוקדם'},
  ];

  const [dailyLine, setDailyLine] = useState('');
  const [dailyEpisode, setDailyEpisode] = useState(null);
  const [episodesError, setEpisodesError] = useState('');

  const [mood, setMood] = useState(() => {
    try { return localStorage.getItem('profile.mood') || ''; } catch { return ''; }
  });

  const [habits, setHabits] = useState(() => {
    try { return JSON.parse(localStorage.getItem('profile.habits') || '{}'); } catch { return {}; }
  });

  const [reflection1, setReflection1] = useState(() => {
    try { return localStorage.getItem('profile.reflection1') || ''; } catch { return ''; }
  });
  const [reflection2, setReflection2] = useState(() => {
    try { return localStorage.getItem('profile.reflection2') || ''; } catch { return ''; }
  });

  const openLink = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const todayKeyLocal = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getOrPickDailyIndex = (storageKey, length) => {
    const day = todayKeyLocal();
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.day === day && typeof parsed?.index === 'number' && parsed.index >= 0 && parsed.index < length) {
          return parsed.index;
        }
      }
    } catch {}
    const index = Math.floor(Math.random() * length);
    try { localStorage.setItem(storageKey, JSON.stringify({day, index})); } catch {}
    return index;
  };

  const loadEpisodesCached = async () => {
    const cacheKey = 'wellness.episodes.cache.v1';
    const ttlMs = 6 * 60 * 60 * 1000;

    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.savedAt && Array.isArray(cached?.episodes) && (Date.now() - cached.savedAt) < ttlMs) {
          return cached.episodes;
        }
      }
    } catch {}

    const resp = await fetch('/api/wellness-episodes');
    const data = await resp.json();
    if (!resp.ok) throw new Error(data?.error || 'Failed to load episodes');

    const episodes = Array.isArray(data?.episodes) ? data.episodes : [];
    try { localStorage.setItem(cacheKey, JSON.stringify({savedAt: Date.now(), episodes})); } catch {}
    return episodes;
  };

  useEffect(() => {
    const lineIdx = getOrPickDailyIndex('profile.dailyLine', RELAXING_LINES.length);
    setDailyLine(RELAXING_LINES[lineIdx]);

    (async () => {
      const episodes = await loadEpisodesCached();
      if (!episodes.length) throw new Error('No episodes returned');
      const epIdx = getOrPickDailyIndex('profile.dailyEpisode', episodes.length);
      setDailyEpisode(episodes[epIdx]);
    })().catch((e) => {
      setEpisodesError(e?.message || 'בעיה בטעינת הפרקים');
      setDailyEpisode(null);
    });
  }, []);

  const replaceEpisodeForToday = async () => {
    try { localStorage.removeItem('profile.dailyEpisode'); } catch {}
    try {
      const episodes = await loadEpisodesCached();
      if (!episodes.length) return;
      const epIdx = getOrPickDailyIndex('profile.dailyEpisode', episodes.length);
      setDailyEpisode(episodes[epIdx]);
    } catch {}
  };

  const setMoodAndPersist = (value) => {
    setMood(value);
    try { localStorage.setItem('profile.mood', value); } catch {}
  };

  const toggleHabit = (key) => {
    const next = {...habits, [key]: !habits[key]};
    setHabits(next);
    try { localStorage.setItem('profile.habits', JSON.stringify(next)); } catch {}
  };

  const updateReflection1 = (v) => {
    setReflection1(v);
    try { localStorage.setItem('profile.reflection1', v); } catch {}
  };
  const updateReflection2 = (v) => {
    setReflection2(v);
    try { localStorage.setItem('profile.reflection2', v); } catch {}
  };

  const handleTogglePush = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
        success('התראות Push בוטלו');
      } else {
        await subscribe();
        success('התראות Push הופעלו');
      }
    } catch (err) {
      error(err.message || 'שגיאה בהפעלת התראות');
    }
  };

  const handleTestPush = async () => {
    try {
      await sendTestNotification('בדיקת התראה', 'זוהי התראה בדיקה מ-CalPro');
      success('התראה נשלחה');
    } catch (err) {
      error(err.message || 'שגיאה בשליחת התראה');
    }
  };

  const handleLogout = async () => {
    try {
      const result = await signOut();
      if (result.success) {
        success('Logged out successfully');
        navigate('/login', { replace: true });
      } else {
        error(result.error || 'Failed to log out');
      }
    } catch (err) {
      error('An error occurred while logging out');
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.userName}>{userNickname} ❤️</div>
          <button
            style={styles.backButton}
            onClick={() => navigate('/')}>
            ←
          </button>
        </div>
        <div style={styles.headerCenter}>
          <div style={styles.profileIcon}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <path d="M12 16 Q20 8 28 16" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <circle cx="20" cy="20" r="3" fill={colors.primary}/>
            </svg>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.bellIcon}>🔔</div>
        </div>
      </div>

      {/* Title */}
      <div style={styles.titleSection}>
        <div style={styles.title}>הפרופיל שלי</div>
      </div>

      {/* Calm / Wellness */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>רוגע ובריאות</h3>
        <div style={styles.dailyLine}>{dailyLine}</div>

        <div style={styles.card}>
          <div style={styles.cardTop}>
            <div style={{flex: 1}}>
              <div style={styles.cardLabel}>פרק מומלץ להיום</div>
              <div style={styles.cardTitle}>
                {dailyEpisode?.name || (episodesError ? 'לא הצלחנו לטעון היום פרק' : 'טוען פרק...')}
              </div>
              {episodesError && process.env.NODE_ENV === 'development' && (
                <div style={styles.debugError}>
                  {episodesError}
                </div>
              )}
              <div style={styles.cardSub}>
                {dailyEpisode?.release_date ? `פורסם: ${dailyEpisode.release_date}` : ''}
              </div>
            </div>
            <button
              style={styles.primaryButton}
              onClick={() => (dailyEpisode?.url ? openLink(dailyEpisode.url) : openLink(SHOW_LINK))}
            >
              האזנה
            </button>
          </div>

          <div style={styles.cardActions}>
            <button style={styles.secondaryButton} onClick={() => openLink(SHOW_LINK)}>
              לכל הפרקים
            </button>
            <button style={styles.secondaryButton} onClick={replaceEpisodeForToday}>
              החלף להיום
            </button>
          </div>
        </div>

        <div style={styles.subTitle}>איך הולך היום?</div>
        <div style={styles.row3}>
          {MOODS.map((m) => (
            <button
              key={m}
              onClick={() => setMoodAndPersist(m)}
              style={{
                ...styles.chip,
                borderColor: mood === m ? colors.primary : colors.border,
                color: mood === m ? colors.primary : colors.textPrimary,
              }}
            >
              {m}
            </button>
          ))}
        </div>

        <div style={styles.subTitle}>הרגלים קטנים (בלי לחץ)</div>
        <div style={styles.grid2}>
          {HABITS.map((h) => (
            <button
              key={h.k}
              onClick={() => toggleHabit(h.k)}
              style={{
                ...styles.chip,
                textAlign: 'right',
                borderColor: habits[h.k] ? colors.success : colors.border,
                color: habits[h.k] ? colors.success : colors.textPrimary,
              }}
            >
              {habits[h.k] ? '✓ ' : ''}{h.t}
            </button>
          ))}
        </div>

        <div style={styles.subTitle}>רפלקציה קצרה</div>
        <textarea
          value={reflection1}
          onChange={(e) => updateReflection1(e.target.value)}
          placeholder="מה עשה לי טוב השבוע?"
          style={styles.textarea}
        />
        <textarea
          value={reflection2}
          onChange={(e) => updateReflection2(e.target.value)}
          placeholder="מה אני רוצה לשמור לשבוע הבא?"
          style={styles.textarea}
        />
      </div>

      {/* Push Notifications Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>התראות Push</h3>
        <div style={styles.settingRow}>
          <span style={styles.settingLabel}>התראות Push</span>
          <button
            onClick={handleTogglePush}
            style={{
              ...styles.toggleButton,
              backgroundColor: isSubscribed ? colors.success : colors.textSecondary,
            }}>
            {isSubscribed ? 'מופעל' : 'כבוי'}
          </button>
        </div>
        {isSubscribed && (
          <button 
            onClick={handleTestPush} 
            style={styles.testButton}>
            שלח התראה בדיקה
          </button>
        )}
      </div>

      {/* Account Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Account</h3>
        {user && (
          <div style={styles.accountInfo}>
            <div style={styles.accountLabel}>Email</div>
            <div style={styles.accountValue}>{user.email}</div>
          </div>
        )}
        <button 
          onClick={handleLogout} 
          style={styles.logoutButton}>
          Log Out
        </button>
      </div>

      {/* Build Info - Only in development */}
      {window.__CALPRO_BUILD__ && process.env.NODE_ENV === 'development' && (
        <div style={styles.buildInfo}>
          <div style={styles.buildInfoText}>
            Build: {window.__CALPRO_BUILD__.time}
          </div>
          <div style={styles.buildInfoText}>
            Version: {window.__CALPRO_BUILD__.version}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    paddingTop: '20px',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.textPrimary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  profileIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.surface,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px solid ${colors.primary}`,
  },
  headerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bellIcon: {
    fontSize: 24,
    color: colors.textPrimary,
  },
  titleSection: {
    padding: '20px',
    paddingTop: '10px',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.textPrimary,
    textAlign: 'center',
  },
  section: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 20,
    margin: '0 20px 16px',
    border: `1px solid ${colors.border}`,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 16,
  },
  dailyLine: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 12,
  },
  card: {
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  cardTop: {
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardLabel: { color: colors.textSecondary, fontSize: 12, marginBottom: 6 },
  cardTitle: {
    color: colors.primary,
    fontWeight: 900,
    fontSize: 16,
    marginBottom: 6,
    lineHeight: 1.3,
  },
  cardSub: { color: colors.textPrimary, fontSize: 12, opacity: 0.85 },
  debugError: { color: colors.textSecondary, fontSize: 11, opacity: 0.75, marginTop: 6, lineHeight: 1.3 },
  cardActions: { display: 'flex', gap: 10, marginTop: 12 },
  primaryButton: {
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    color: colors.textOnPrimary,
    fontWeight: 900,
    cursor: 'pointer',
    minWidth: 80,
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: '10px 12px',
    color: colors.textPrimary,
    fontWeight: 700,
    cursor: 'pointer',
  },
  subTitle: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: 800,
    marginTop: 6,
    marginBottom: 10,
  },
  row3: { display: 'flex', gap: 10, marginBottom: 14 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 },
  chip: {
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '10px 10px',
    cursor: 'pointer',
    fontWeight: 800,
  },
  textarea: {
    width: '100%',
    minHeight: 64,
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '12px 12px',
    color: colors.textPrimary,
    outline: 'none',
    resize: 'vertical',
    marginBottom: 10,
    fontSize: 14,
    lineHeight: 1.4,
  },
  settingRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  settingLabel: {
    fontSize: 16,
    color: colors.textPrimary,
    flex: 1,
  },
  toggleButton: {
    backgroundColor: colors.textSecondary,
    border: 'none',
    borderRadius: 8,
    padding: '8px 20px',
    color: colors.textOnPrimary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    minWidth: 80,
  },
  testButton: {
    width: '100%',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.primary}`,
    borderRadius: 8,
    padding: '10px 16px',
    color: colors.primary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8,
  },
  buildInfo: {
    margin: '20px',
    padding: '12px',
    backgroundColor: colors.surface,
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
  },
  buildInfoText: {
    fontSize: '10px',
    color: colors.textSecondary,
    marginBottom: '4px',
    fontFamily: 'monospace',
  },
  accountInfo: {
    marginBottom: 16,
  },
  accountLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  accountValue: {
    fontSize: 16,
    color: colors.textPrimary,
    fontWeight: 500,
  },
  logoutButton: {
    width: '100%',
    backgroundColor: colors.error,
    border: 'none',
    borderRadius: 8,
    padding: '12px 16px',
    color: colors.textOnPrimary,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default ProfileScreen;

